<template>
  <div
    v-show="hasAnnouncement && !hidden"
    class="site-notice has-background-info has-text-white no-print"
    @click="showContent = !showContent"
  >
    <div class="section is-info">
      <button class="delete" :aria-label="$gettext('Close')" @click="hide" />
      <div ref="header" />
      <div v-show="showContent" ref="content" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';

import forum from '@/js/apis/forum';
import { useGlobalProperties } from '@/js/composables/global-properties';
import { sanitizeHtml } from '@/js/sanitize-html';
import { getPreferences } from '@/js/vue-plugins/local-storage';

// stored under the component name, as before: existing preferences are kept
const preferences = getPreferences('SiteNotice');

const { $user } = useGlobalProperties();

const hasAnnouncement = ref(false);
const hidden = ref(false);
const showContent = ref(false);
const updatedAt = ref<string | null>(null);
// store lang, because if user changes lang after getting notice...
const lang = ref($user.lang);
const contentRef = useTemplateRef<HTMLElement>('content');
const headerRef = useTemplateRef<HTMLElement>('header');

const readdenPostKey = computed({
  get() {
    return preferences.get('readdenPostKey.' + lang.value);
  },
  set(value) {
    preferences.set('readdenPostKey.' + lang.value, value);
  },
});

function loadAnnouncement() {
  forum
    .readAnnouncement(lang.value)
    .then(({ topic, post }) => {
      if (forum.hasTag(topic, 'visible')) {
        updatedAt.value = post.updated_at;

        if (readdenPostKey.value === post.updated_at) {
          return;
        }

        hasAnnouncement.value = true;

        // compute html, to split p
        // post.cooked is HTML generated by the forum from a user's post: sanitize it
        // before injecting it in the DOM to prevent stored XSS.
        const content = document.createElement('div');
        content.innerHTML = sanitizeHtml(post.cooked);
        const paragraphs = content.getElementsByTagName('p');

        headerRef.value!.appendChild(paragraphs[0]);

        for (const p of Array.from(paragraphs).slice(1)) {
          contentRef.value!.appendChild(p);
        }
      }
    })
    .catch((error) => {
      console.error('Error retrieving site announcement :', error);
    });
}

function hide() {
  hidden.value = true;
  readdenPostKey.value = updatedAt.value;
}

// created
loadAnnouncement();
</script>

<style lang="scss">
.site-notice {
  a,
  a:hover {
    color: currentColor;
    text-decoration: underline;
  }
}
</style>

<style scoped lang="scss">
.delete {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}
</style>
